/** A record Task keeps the key already sent in its kit draft, including reload. */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { TabDraft } from "@/components/ui/box-draft";
import type { Result } from "@/components/ui/box.schema";
import { ApiError } from "@/lib/api/client";
import { checkCommit, reasonOnError } from "@/lib/api/commitSeams";
import { currentOnConflict } from "@/lib/api/conflict";
import type { Course } from "@/lib/api/courses";
import { invalidateCourseDependentQueries } from "@/lib/course-query-keys";

export interface SectionPending { key: string; action: string; course: string; effects: string[]; remains: string[] }
export interface SectionSaved { section_id: string; version?: string; created_id?: string; receipt_id?: string }

export function useSectionTaskResults(onConfirmed?: (sent: SectionPending, saved: Omit<SectionSaved, "section_id">) => void) {
  const client = useQueryClient();
  const [results, setResults] = useState<Record<string, Result>>({});
  const [created, setCreated] = useState<Record<string, string>>({});
  const refresh = async (id: string) => {
    invalidateCourseDependentQueries(client, id);
    void client.invalidateQueries({ queryKey: ["course-record-review", id] });
    await client.invalidateQueries({ queryKey: ["course", id] });
  };
  const hold = (id: string, result: Result) => { setResults(was => ({ ...was, [id]: result })); return result; };
  const unknown = <T,>(id: string, draft: TabDraft<T, SectionPending>, sent: SectionPending): Result => ({
    state: "unknown", cap: "Not confirmed", requestId: sent.key,
    sentence: "No confirmation was received. The draft is kept. Check this same request before continuing.",
    check: { verb: "Check", note: "Reads the saved receipt without sending the change again.", run: async () => {
      try {
        const checked = await checkCommit<SectionSaved>(sent.key, value => {
          if (!value || typeof value !== "object" || !("section_id" in value) || typeof value.section_id !== "string") throw new TypeError("The section receipt could not be read.");
          return value as SectionSaved;
        });
        if (checked.state === "not_committed") {
          draft.setPending(null);
          return hold(id, { state: "refused", cap: "Not recorded", sentence: "The server has no committed change for this request. Review the kept draft before making a new confirmation.", draft: "kept" });
        }
        if (checked.state !== "confirmed") return hold(id, unknown(id, draft, sent));
        if (checked.receipt.action !== sent.action || !checked.receipt.subject_ids.includes(sent.course)) throw new TypeError("This receipt belongs to another change.");
        const saved = checked.receipt.result;
        if (saved.section_id !== sent.course) throw new TypeError("This receipt names another section.");
        if (saved.created_id) setCreated(was => ({ ...was, [id]: saved.created_id! }));
        draft.setPending(null);
        onConfirmed?.(sent, saved);
        await refresh(sent.course);
        return hold(id, { state: "confirmed", at: checked.receipt.recorded_at, effects: sent.effects, remains: [...sent.remains, ...(saved.receipt_id ? [`Audit receipt ${saved.receipt_id} is kept.`] : [])], version: saved.version });
      } catch { return hold(id, unknown(id, draft, sent)); }
    } },
  });
  return {
    created,
    reset: (id: string) => { setResults(was => ({ ...was, [id]: { state: "none" } })); },
    result: <T,>(id: string, draft: TabDraft<T, SectionPending>): Result => draft.pending ? unknown(id, draft, draft.pending) : results[id] ?? { state: "none" },
    run: async <T,>(id: string, draft: TabDraft<T, SectionPending>, sent: SectionPending, send: (markSent: () => void) => Promise<{ version?: string; created_id?: string; receipt_id?: string }>): Promise<Result> => {
      if (draft.pending) return hold(id, unknown(id, draft, draft.pending));
      let didSend = false;
      try {
        const saved = await send(() => { didSend = true; draft.setPending(sent); });
        if (saved.created_id) setCreated(was => ({ ...was, [id]: saved.created_id! }));
        draft.setPending(null);
        onConfirmed?.(sent, saved);
        await refresh(sent.course);
        return hold(id, { state: "confirmed", at: new Date().toISOString(), effects: sent.effects, remains: [...sent.remains, ...(saved.receipt_id ? [`Audit receipt ${saved.receipt_id} is kept.`] : [])], version: saved.version });
      } catch (error) {
        if (!didSend) return hold(id, { state: "refused", cap: "Not sent", sentence: error instanceof Error ? error.message : "Review this change before continuing.", draft: "kept" });
        const theirs = currentOnConflict<Course>(error);
        if (theirs) client.setQueryData(["course", theirs.id], theirs);
        if (error instanceof ApiError && error.status >= 400 && error.status < 500 && ![408, 429].includes(error.status)) {
          draft.setPending(null);
          return hold(id, { state: "refused", cap: "Not changed", sentence: reasonOnError(error)?.message ?? error.message, draft: "kept" });
        }
        return hold(id, unknown(id, draft, sent));
      }
    },
  };
}
